/**
 * Aufgaben Liste - mit Mehrfachauswahl & Aktionsleiste
 * Aufgabenverwaltung 2.0 (27.10.2026): serverseitige Suche/Filter/Sortierung mit Seiten, Auswahl über Filterwechsel erhalten,
 * Sammel-Erledigung und intelligente Zusammenführung nur mit Vorschau + Bestätigung, Dublettenhinweis, „ZEO zu Aufgaben befragen“.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AutopilotBefundListe from '@/components/AutopilotBefundListe';
import { useTabSearchParams } from '@/hooks/useTabRoute';
import { gruppiereAufgaben, fortschritt } from '@/lib/aufgabenBaum';
import { umschalten, alleGefilterten, alleGefiltertenGewaehlt, nur, einKlient, zusammenfuehrbar } from '@/lib/aufgabenAuswahl';
import { HERKUNFT_FARBEN } from '@/lib/aufgabenHerkunft';
import SammelErledigenDialog from '@/components/aufgaben/SammelErledigenDialog';
import ZusammenfuehrenDialog from '@/components/aufgaben/ZusammenfuehrenDialog';
import AufgabenZeoFrage from '@/components/aufgaben/AufgabenZeoFrage';
import {
  CheckSquare,
  Plus,
  Filter,
  MoreVertical,
  Trash2,
  RefreshCw,
  Check,
  Clock,
  AlertTriangle,
  UserCheck,
  CalendarDays,
  ChevronRight,
  RotateCcw,
  X,
  CheckCheck,
  ArrowRight,
  ChevronUp,
  ChevronDown,
  Layers,
  Search,
  Bot,
  Copy,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { betreuungAPI } from '@/api/betreuungAPI';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { de } from 'date-fns/locale';

const STATUS_OPTIONS = [
  { value: 'alle', label: 'Alle Status' },
  { value: 'offen', label: 'Offen (alle offenen)' },
  { value: 'in_bearbeitung', label: 'In Bearbeitung' },
  { value: 'vorschlag', label: 'KI-Aufgabenvorschläge' },
  { value: 'erledigt', label: 'Erledigt' }
];

const FAELLIG_OPTIONS = [
  { value: 'alle', label: 'Jede Fälligkeit' },
  { value: 'ueberfaellig', label: 'Überfällig' },
  { value: 'heute', label: 'Heute fällig' },
  { value: 'bald', label: 'Bald fällig (7 Tage)' },
  { value: 'ohne', label: 'Ohne Fälligkeit' },
];

const PRO_SEITE = 100;

const PRIORITAET_OPTIONS = [
  { value: 'alle', label: 'Alle Prioritäten' },
  { value: 'dringend', label: 'Dringend' },
  { value: 'hoch', label: 'Hoch' },
  { value: 'normal', label: 'Normal' },
  { value: 'niedrig', label: 'Niedrig' }
];

const PRIORITAET_COLORS = {
  niedrig: 'bg-green-500 text-white',
  normal: 'bg-blue-100 text-blue-700',
  mittel: 'bg-orange-400 text-white',
  hoch: 'bg-orange-400 text-white',
  dringend: 'bg-red-500 text-white'
};

const STATUS_COLORS = {
  offen: 'border-amber-300 bg-amber-50',
  in_bearbeitung: 'border-blue-300 bg-blue-50',
  erledigt: 'border-green-300 bg-green-50',
  abgebrochen: 'border-slate-300 bg-slate-50'
};

const SORT_OPTIONS = [
  { value: 'betreuter_asc', label: 'Betreuter A-Z' },
  { value: 'betreuter_desc', label: 'Betreuter Z-A' },
  { value: 'titel_asc', label: 'Titel A-Z' },
  { value: 'faellig_asc', label: 'Fälligkeit aufsteigend' },
  { value: 'faellig_desc', label: 'Fälligkeit absteigend' },
  { value: 'prioritaet', label: 'Nach Priorität' },
  { value: 'created_desc', label: 'Neueste zuerst' },
  { value: 'updated_desc', label: 'Zuletzt geändert' },
];

const herkunftLabel = (h) => HERKUNFT_FARBEN[h]?.label || h;

const ZUWEISUNG_OPTIONS = [
  { value: 'alle', label: 'Alle Aufgaben' },
  { value: 'mir', label: 'Mir zugewiesen' },
  { value: 'unzugewiesen', label: 'Nicht zugewiesen' },
];

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function AufgabenPage() {
  const [searchParams, setSearchParams] = useTabSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isSachbearbeiter = user?.rolle === 'sachbearbeiter';
  const defaultZuweisung = isSachbearbeiter ? 'mir' : (searchParams.get('zuweisung') || 'alle');

  const [aufgaben, setAufgaben] = useState([]);
  const [treffer, setTreffer] = useState([]);           // alle gefilterten (id + Klient) für „Alle gefilterten auswählen“
  const [gesamt, setGesamt] = useState(0);
  const [seite, setSeite] = useState(1);
  const [facetten, setFacetten] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'alle');
  const [prioritaetFilter, setPrioritaetFilter] = useState(searchParams.get('prioritaet') || 'alle');
  const [zuweisungFilter, setZuweisungFilter] = useState(defaultZuweisung);
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'betreuter_asc');
  const [faelligFilter, setFaelligFilter] = useState(searchParams.get('faelligkeit') || 'alle');
  const [herkunftFilter, setHerkunftFilter] = useState(searchParams.get('herkunft') || 'alle');
  const [vorgangFilter, setVorgangFilter] = useState(searchParams.get('vorgang_id') || 'alle');
  const [klientFilter, setKlientFilter] = useState(searchParams.get('klient_id') || 'alle');
  const [autopilotFilter, setAutopilotFilter] = useState(searchParams.get('autopilot') === '1');   // Filter „Autopilot-Befunde“
  const [suchtext, setSuchtext] = useState(searchParams.get('q') || '');
  const [q, setQ] = useState(searchParams.get('q') || '');

  // Auswahl-State: Map aufgabe_id -> Kurzangaben; bleibt über Such-/Filterwechsel erhalten (lib/aufgabenAuswahl)
  const [selected, setSelected] = useState(new Map());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [aufgeklappt, setAufgeklappt] = useState(new Set());
  const [erledigenIds, setErledigenIds] = useState(null);
  const [mergeIds, setMergeIds] = useState(null);
  const [zeoOffen, setZeoOffen] = useState(false);
  const [zeoModus, setZeoModus] = useState('auswahl');
  const [dubletten, setDubletten] = useState(null);
  const ladeNr = useRef(0);

  const klientId = klientFilter !== 'alle' ? klientFilter : null;
  const klientName = klientId ? ((facetten.klienten || []).find(k => k.id === klientId)?.name || aufgaben.find(a => a.klient_id === klientId)?.betreuter_name
    || aufgaben.find(a => a.klient_id === klientId)?.klient_name || '') : '';

  const filterParams = () => ({
    q, status: statusFilter, prioritaet: prioritaetFilter, faelligkeit: faelligFilter, herkunft: herkunftFilter,
    vorgang_id: vorgangFilter, klient_id: klientFilter, zuweisung: zuweisungFilter, sort: sortBy,
  });

  const loadAufgaben = async (naechsteSeite = 1) => {
    const nr = ++ladeNr.current;
    setLoading(true);
    try {
      const r = await betreuungAPI.sucheAufgaben({ ...filterParams(), seite: naechsteSeite, pro_seite: PRO_SEITE });
      if (nr !== ladeNr.current) return;                  // veraltete Antwort (Filter inzwischen geändert)
      setAufgaben(prev => naechsteSeite > 1 ? [...prev, ...r.items] : r.items);
      setTreffer(r.ids || []);
      setGesamt(r.gesamt || 0);
      setSeite(naechsteSeite);
      if (!klientId || !facetten.klienten) setFacetten(prev => ({ ...r.facetten, klienten: r.facetten.klienten || prev.klienten }));
    } catch {
      toast.error('Fehler beim Laden');
    } finally {
      if (nr === ladeNr.current) setLoading(false);
    }
  };

  const ladeDubletten = async () => {
    if (!klientId) { setDubletten(null); return; }
    try { setDubletten(await betreuungAPI.aufgabenDubletten(klientId)); } catch { setDubletten(null); }
  };

  const neuLaden = () => { loadAufgaben(1); ladeDubletten(); };

  // Stichwortsuche entprellt
  useEffect(() => {
    const t = setTimeout(() => setQ(suchtext.trim()), 300);
    return () => clearTimeout(t);
  }, [suchtext]);

  useEffect(() => {
    loadAufgaben(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, statusFilter, prioritaetFilter, zuweisungFilter, sortBy, faelligFilter, herkunftFilter, vorgangFilter, klientFilter]);

  useEffect(() => {
    ladeDubletten();
    if (!klientId && zeoModus === 'klient') setZeoModus('auswahl');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [klientFilter]);

  const setParam = (key, value) => {
    if (!value || value === 'alle') searchParams.delete(key);
    else searchParams.set(key, value);
    setSearchParams(searchParams);
  };

  const handleStatusChange = (value) => { setStatusFilter(value); setParam('status', value); };
  const handlePrioritaetChange = (value) => { setPrioritaetFilter(value); setParam('prioritaet', value); };

  // Auswahl-Checkbox: wählt NUR aus – erledigt nie eine Aufgabe
  const toggleSelect = useCallback((aufgabe, e) => {
    e?.stopPropagation();
    setSelected(prev => umschalten(prev, aufgabe));
  }, []);

  const selectAll = () => setSelected(prev => alleGefilterten(prev, treffer));
  const alleGewaehlt = alleGefiltertenGewaehlt(selected, treffer);

  // Batch-Operationen
  const batchUpdate = async (update, successMsg) => {
    setBatchLoading(true);
    try {
      const ids = [...selected.keys()];
      const r = await Promise.allSettled(ids.map(id => betreuungAPI.updateAufgabe(id, update)));
      const fehler = r.filter(x => x.status === 'rejected').length;
      if (fehler) toast.error(`${ids.length - fehler} von ${ids.length} geändert – ${fehler} fehlgeschlagen`);
      else toast.success(successMsg);
      setSelected(new Map());
      neuLaden();
    } finally {
      setBatchLoading(false);
    }
  };

  const batchReopen = () => batchUpdate({ status: 'offen' }, `${selected.size} Aufgabe(n) wieder geöffnet`);

  const zeoAnalysieren = () => {
    if (!einKlient(selected)) { toast.error('ZEO-Analyse: bitte Aufgaben genau eines Klienten auswählen.'); return; }
    setZeoModus('auswahl');
    setZeoOffen(true);
    setTimeout(() => document.querySelector('[data-testid="zeo-frage-bereich"]')?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  const mergeStarten = (liste) => {
    const m = liste ? nur(liste) : selected;
    if (!zusammenfuehrbar(m)) { toast.error('Zusammenführen: mindestens zwei Aufgaben desselben Klienten auswählen.'); return; }
    if (liste) setSelected(m);
    setMergeIds([...m.keys()]);
  };

  const erledigenStarten = (liste) => {
    const m = liste ? nur(liste) : selected;
    if (!m.size) return;
    if (liste) setSelected(m);
    setErledigenIds([...m.keys()]);
  };

  const batchVerschieben = (tage) => {
    const datum = addDays(new Date(), tage);
    batchUpdate({ faellig_am: datum }, `${selected.size} Aufgabe(n) verschoben auf ${new Date(datum).toLocaleDateString('de-DE')}`);
  };

  const batchVerschiebenDatum = (date) => {
    if (!date) return;
    const datum = date.toISOString().slice(0, 10);
    setCalendarOpen(false);
    batchUpdate({ faellig_am: datum }, `${selected.size} Aufgabe(n) verschoben auf ${date.toLocaleDateString('de-DE')}`);
  };

  // Einzel-Aktionen (Dropdown)
  const handleMarkErledigt = async (aufgabe, e) => {
    e?.stopPropagation();
    try {
      await betreuungAPI.updateAufgabe(aufgabe.id, { status: 'erledigt' });
      toast.success('Aufgabe erledigt');
      loadAufgaben();
    } catch {
      toast.error('Fehler');
    }
  };

  const handleReopen = async (aufgabe, e) => {
    e?.stopPropagation();
    try {
      await betreuungAPI.updateAufgabe(aufgabe.id, { status: 'offen' });
      toast.success('Aufgabe wieder geöffnet');
      loadAufgaben();
    } catch {
      toast.error('Fehler');
    }
  };

  const handleDelete = async (aufgabe, e) => {
    e?.stopPropagation();
    try {
      await betreuungAPI.deleteAufgabe(aufgabe.id);
      toast.success('Aufgabe gelöscht');
      loadAufgaben();
    } catch {
      toast.error('Fehler');
    }
  };

  const hasSelection = selected.size > 0;
  const dublettenAnzahl = dubletten?.anzahl_aufgaben || 0;

  // Eine Aufgabenzeile (Haupt- oder Unteraufgabe)
  const zeile = (aufgabe, unter = false) => {
            const isSelected = selected.has(aufgabe.id);
            return (
              <div
                key={aufgabe.id}
                className={`bg-white rounded-xl ${unter ? 'p-3 ml-8 border' : 'p-4 border-2'} cursor-pointer hover:shadow-md transition-all ${
                  isSelected
                    ? 'border-blue-400 bg-blue-50/50 ring-1 ring-blue-200'
                    : STATUS_COLORS[aufgabe.status] || 'border-slate-200'
                }`}
                onClick={() => navigate(`/aufgaben/${aufgabe.id}`)}
                data-testid={`aufgabe-row-${aufgabe.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    {/* Auswahl-Checkbox */}
                    <button
                      role="checkbox"
                      aria-checked={isSelected}
                      aria-label="Aufgabe auswählen"
                      title="Auswählen (ändert die Aufgabe nicht)"
                      onClick={(e) => toggleSelect(aufgabe, e)}
                      data-testid={`aufgabe-select-${aufgabe.id}`}
                      className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                        isSelected ? 'bg-blue-500 border-blue-500 text-white' : 'border-slate-300 hover:border-blue-400 bg-white'
                      }`}
                    >
                      {isSelected && <Check className="w-4 h-4" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium ${aufgabe.status === 'erledigt' ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                        {aufgabe.titel}
                        {aufgabe.status === 'erledigt' && <Badge variant="outline" className="ml-2 text-xs text-green-700 border-green-300">erledigt</Badge>}
                        {['aufgabenvorschlag_frist_pruefen', 'zuordnung_pruefen'].includes(aufgabe.status) && (
                          <Badge variant="outline" className="ml-2 text-xs text-purple-700 border-purple-300">KI-Vorschlag</Badge>
                        )}
                      </p>
                      {aufgabe.beschreibung && (
                        <p className="text-sm text-slate-500 mt-1">{aufgabe.beschreibung}</p>
                      )}
                      <div className="flex items-center flex-wrap gap-3 mt-2 text-sm text-slate-500">
                        {aufgabe.faellig_am && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(aufgabe.faellig_am).toLocaleDateString('de-DE')}
                          </span>
                        )}
                        {(aufgabe.betreuter_name || aufgabe.klient_name) && (
                          <button className="hover:underline" title="Nach diesem Klienten filtern" data-testid={`klient-filter-${aufgabe.id}`}
                            onClick={(e) => { e.stopPropagation(); if (aufgabe.klient_id) { setKlientFilter(aufgabe.klient_id); setParam('klient_id', aufgabe.klient_id); } }}>
                            {aufgabe.betreuter_name || aufgabe.klient_name}
                          </button>
                        )}
                        {aufgabe.zugewiesen_an_name && (
                          <Badge variant="outline" className="text-xs flex items-center gap-1">
                            <UserCheck className="w-3 h-3" />
                            {aufgabe.zugewiesen_an_name}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${PRIORITAET_COLORS[aufgabe.prioritaet] || PRIORITAET_COLORS.normal}`}>
                      {aufgabe.prioritaet || 'normal'}
                    </span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" data-testid={`aufgabe-menu-${aufgabe.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        {aufgabe.status !== 'erledigt' ? (
                          <DropdownMenuItem onClick={(e) => handleMarkErledigt(aufgabe, e)} data-testid="menu-erledigt">
                            <Check className="w-4 h-4 mr-2" />
                            Als erledigt markieren
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={(e) => handleReopen(aufgabe, e)} data-testid="menu-reopen">
                            <RotateCcw className="w-4 h-4 mr-2" />
                            Wieder öffnen
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger>
                            <ArrowRight className="w-4 h-4 mr-2" />
                            Verschieben auf
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent>
                            <DropdownMenuItem onClick={async (e) => { e.stopPropagation(); await betreuungAPI.updateAufgabe(aufgabe.id, { faellig_am: addDays(new Date(), 1) }); toast.success('Verschoben auf morgen'); loadAufgaben(); }}>
                              Morgen
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={async (e) => { e.stopPropagation(); await betreuungAPI.updateAufgabe(aufgabe.id, { faellig_am: addDays(new Date(), 2) }); toast.success('Verschoben auf übermorgen'); loadAufgaben(); }}>
                              Übermorgen
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={async (e) => { e.stopPropagation(); await betreuungAPI.updateAufgabe(aufgabe.id, { faellig_am: addDays(new Date(), 7) }); toast.success('Verschoben auf nächste Woche'); loadAufgaben(); }}>
                              In einer Woche
                            </DropdownMenuItem>
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-red-600" onClick={(e) => handleDelete(aufgabe, e)} data-testid="menu-delete">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Löschen
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            );
  };


  return (
    <div className="space-y-6" data-testid="aufgaben-page">
      {/* Info-Banner für Sachbearbeiter */}
      {isSachbearbeiter && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <UserCheck className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-blue-800">Ihre zugewiesenen Aufgaben</p>
            <p className="text-sm text-blue-600 mt-1">
              Als Sachbearbeiter sehen Sie nur Aufgaben, die Ihnen zugewiesen wurden.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Aufgaben</h1>
          <p className="text-slate-500 mt-1" data-testid="aufgaben-anzahl">
            {gesamt} Treffer{aufgaben.length < gesamt ? ` · ${aufgaben.length} geladen` : ''}
            {(() => { const u = aufgaben.length - gruppiereAufgaben(aufgaben).length; return u > 0 ? ` · ${u} als Unteraufgaben eingerückt` : ''; })()}
          </p>
        </div>
        <Link to="/aufgaben/neu">
          <Button data-testid="new-aufgabe-btn">
            <Plus className="w-4 h-4 mr-2" />
            Neue Aufgabe
          </Button>
        </Link>
      </div>

      {/* Suche */}
      <div className="relative max-w-2xl">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input className="pl-9" value={suchtext} onChange={(e) => { setSuchtext(e.target.value); setParam('q', e.target.value.trim()); }}
          placeholder="Suchen in Titel, Beschreibung, Institution, Personen, Vorgang, Dokumenttiteln …" data-testid="aufgaben-suche" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <Select value={faelligFilter} onValueChange={(v) => { setFaelligFilter(v); setParam('faelligkeit', v); }}>
          <SelectTrigger className="w-44" data-testid="faellig-filter">
            <Clock className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Fälligkeit" />
          </SelectTrigger>
          <SelectContent>
            {FAELLIG_OPTIONS.map((opt) => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {(facetten.klienten?.length > 0 || klientId) && (
          <Select value={klientFilter} onValueChange={(v) => { setKlientFilter(v); setParam('klient_id', v); setVorgangFilter('alle'); setParam('vorgang_id', 'alle'); }}>
            <SelectTrigger className="w-52" data-testid="klient-filter">
              <SelectValue placeholder="Klient" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle Klienten</SelectItem>
              {klientId && !(facetten.klienten || []).some(k => k.id === klientId) && <SelectItem value={klientId}>{klientName || 'Gewählter Klient'}</SelectItem>}
              {(facetten.klienten || []).map(k => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {facetten.vorgaenge?.length > 0 && (
          <Select value={vorgangFilter} onValueChange={(v) => { setVorgangFilter(v); setParam('vorgang_id', v); }}>
            <SelectTrigger className="w-52" data-testid="vorgang-filter">
              <SelectValue placeholder="Vorgang" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle Vorgänge</SelectItem>
              {facetten.vorgaenge.map(v => <SelectItem key={v.id} value={v.id}>{v.titel}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {facetten.herkunft?.length > 0 && (
          <Select value={herkunftFilter} onValueChange={(v) => { setHerkunftFilter(v); setParam('herkunft', v); }}>
            <SelectTrigger className="w-44" data-testid="herkunft-filter">
              <SelectValue placeholder="Herkunft" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Jede Herkunft</SelectItem>
              {facetten.herkunft.map(h => <SelectItem key={h} value={h}>{herkunftLabel(h)}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Button size="sm" variant={autopilotFilter ? 'default' : 'outline'} data-testid="autopilot-filter"
          onClick={() => { const n = !autopilotFilter; setAutopilotFilter(n); setParam('autopilot', n ? '1' : 'alle'); }}>
          Autopilot-Befunde
        </Button>
        <Select value={statusFilter} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-40" data-testid="status-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={prioritaetFilter} onValueChange={handlePrioritaetChange}>
          <SelectTrigger className="w-40" data-testid="prioritaet-filter">
            <AlertTriangle className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Priorität" />
          </SelectTrigger>
          <SelectContent>
            {PRIORITAET_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={(v) => { setSortBy(v); searchParams.set('sort', v); setSearchParams(searchParams); }}>
          <SelectTrigger className="w-48" data-testid="sort-select">
            <SelectValue placeholder="Sortierung" />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={zuweisungFilter} onValueChange={(v) => {
          if (isSachbearbeiter && v === 'alle') return;
          setZuweisungFilter(v);
          searchParams.set('zuweisung', v);
          setSearchParams(searchParams);
        }}>
          <SelectTrigger className="w-44" data-testid="zuweisung-filter">
            <UserCheck className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Zuweisung" />
          </SelectTrigger>
          <SelectContent>
            {ZUWEISUNG_OPTIONS.map((opt) => (
              <SelectItem
                key={opt.value}
                value={opt.value}
                disabled={isSachbearbeiter && opt.value === 'alle'}
              >
                {opt.label}
                {isSachbearbeiter && opt.value === 'alle' && ' (gesperrt)'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={neuLaden} disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
        {treffer.length > 0 && (
          <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-btn"
            className={alleGewaehlt ? 'bg-blue-50 border-blue-300 text-blue-700' : ''}>
            <CheckSquare className="w-4 h-4 mr-1" />
            {alleGewaehlt ? `Alle ${treffer.length} gefilterten abwählen` : `Alle ${treffer.length} gefilterten auswählen`}
          </Button>
        )}
      </div>

      {/* Dublettenhinweis (nur Hinweis, Entscheidung beim Benutzer) */}
      {dublettenAnzahl > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2" data-testid="dubletten-hinweis">
          <p className="text-sm font-medium text-amber-900 flex items-center gap-2"><Copy className="w-4 h-4" />
            {dublettenAnzahl} möglicherweise zusammengehörige Aufgaben gefunden</p>
          {dubletten.gruppen.map((g, i) => (
            <div key={i} className="text-sm bg-white/70 rounded p-2" data-testid={`dubletten-gruppe-${i}`}>
              <div className="text-slate-800">{g.aufgaben.map(a => a.titel).join(' · ')}</div>
              <div className="text-xs text-slate-600">Gemeinsam: {g.gruende.join(', ')}{g.unterschiede.length ? ` · Unterschiede: ${g.unterschiede.join('; ')}` : ''}</div>
              <div className="flex gap-2 mt-1">
                <Button size="sm" variant="outline" onClick={() => setSelected(nur(g.aufgaben))} data-testid={`dubletten-auswaehlen-${i}`}>Auswählen</Button>
                <Button size="sm" variant="outline" onClick={() => mergeStarten(g.aufgaben)} data-testid={`dubletten-pruefen-${i}`}>
                  <Layers className="w-3 h-3 mr-1" />Zusammenführen prüfen
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ZEO zu Aufgaben befragen (derselbe ZEO, nur lesend) */}
      <AufgabenZeoFrage offen={zeoOffen} onOffen={setZeoOffen} klientId={klientId} klientName={klientName} auswahl={selected}
        modus={zeoModus} onModus={setZeoModus}
        onToggle={(a) => setSelected(prev => umschalten(prev, a))} onSelect={(liste) => setSelected(nur(liste))}
        onMerge={mergeStarten} onErledigen={erledigenStarten} onOpen={(id) => navigate(`/aufgaben/${id}`)} onReload={neuLaden} />

      {/* Filter „Autopilot-Befunde“ (ZEO-Autopilot, nur Vorschläge) */}
      {autopilotFilter && <AutopilotBefundListe klientId={klientId} onOpen={(id) => navigate(`/aufgaben/${id}`)} />}

      {/* List */}
      <div className={`space-y-3 ${hasSelection ? 'pb-24' : ''} ${autopilotFilter ? 'hidden' : ''}`}>
        {loading && aufgaben.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : aufgaben.length > 0 ? (
          gruppiereAufgaben(aufgaben).map(({ aufgabe: haupt, kinder, verwaist }) => {
            const f = fortschritt(kinder, haupt);
            const offen = aufgeklappt.has(haupt.id);
            return (
              <div key={haupt.id} className="space-y-2">
                {zeile(haupt)}
                {verwaist && <p className="ml-8 -mt-1 text-xs text-slate-400">Unteraufgabe – Hauptaufgabe ist durch den Filter ausgeblendet</p>}
                {kinder.length > 0 && (
                  <button className="ml-8 text-xs text-blue-700 hover:underline flex items-center gap-1" data-testid={`unteraufgaben-toggle-${haupt.id}`}
                    onClick={() => setAufgeklappt(prev => { const n = new Set(prev); if (n.has(haupt.id)) n.delete(haupt.id); else n.add(haupt.id); return n; })}>
                    {offen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    {f.text}{offen ? '' : ' – anzeigen'}
                  </button>
                )}
                {offen && kinder.map(k => zeile(k, true))}
              </div>
            );
          })
        ) : (
          <div className="bg-white rounded-xl p-12 text-center border border-slate-200">
            <CheckSquare className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 mb-4">Keine Aufgaben gefunden{q ? ` zu „${q}“` : ''}</p>
            <Link to="/aufgaben/neu">
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Neue Aufgabe erstellen
              </Button>
            </Link>
          </div>
        )}
        {aufgaben.length < gesamt && (
          <div className="flex justify-center">
            <Button variant="outline" onClick={() => loadAufgaben(seite + 1)} disabled={loading} data-testid="weitere-laden">
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
              Weitere laden ({aufgaben.length} von {gesamt})
            </Button>
          </div>
        )}
      </div>

      {/* Floating Aktionsleiste */}
      {hasSelection && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-slate-900 text-white px-5 py-3 rounded-2xl shadow-2xl"
          data-testid="batch-action-bar"
        >
          <span className="text-sm font-medium mr-1 whitespace-nowrap">
            {selected.size} ausgewählt
            {(() => { const n = [...selected.keys()].filter(id => !aufgaben.some(x => x.id === id)).length; return n ? ` (${n} nicht sichtbar)` : ''; })()}
          </span>

          <div className="w-px h-6 bg-slate-600" />

          {/* Mit ZEO analysieren (nur lesend) */}
          <Button size="sm" variant="ghost" className="text-purple-300 hover:text-purple-200 hover:bg-slate-800" onClick={zeoAnalysieren}
            disabled={batchLoading} data-testid="batch-zeo-btn">
            <Bot className="w-4 h-4 mr-1" />
            Mit ZEO analysieren
          </Button>

          {/* Intelligent zusammenführen: Vorschau -> Bestätigung -> Paket A */}
          <Button size="sm" variant="ghost" className="text-purple-300 hover:text-purple-200 hover:bg-slate-800" onClick={() => mergeStarten()}
            disabled={batchLoading || !zusammenfuehrbar(selected)} title={zusammenfuehrbar(selected) ? '' : 'Nur mindestens zwei Aufgaben desselben Klienten'}
            data-testid="batch-zusammenfuehren-btn">
            <Layers className="w-4 h-4 mr-1" />
            Intelligent zusammenführen
          </Button>

          {/* Als erledigt markieren: erst Vorschau */}
          <Button size="sm" variant="ghost" className="text-green-400 hover:text-green-300 hover:bg-slate-800" onClick={() => erledigenStarten()}
            disabled={batchLoading} data-testid="batch-erledigt-btn">
            <CheckCheck className="w-4 h-4 mr-1" />
            Als erledigt markieren
          </Button>

          {/* Wieder öffnen */}
          <Button
            size="sm"
            variant="ghost"
            className="text-amber-400 hover:text-amber-300 hover:bg-slate-800"
            onClick={batchReopen}
            disabled={batchLoading}
            data-testid="batch-reopen-btn"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Öffnen
          </Button>

          <div className="w-px h-6 bg-slate-600" />

          {/* Verschieben */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className="text-blue-400 hover:text-blue-300 hover:bg-slate-800"
                disabled={batchLoading}
                data-testid="batch-verschieben-btn"
              >
                <CalendarDays className="w-4 h-4 mr-1" />
                Verschieben
                <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" side="top" className="mb-2">
              <DropdownMenuItem onClick={() => batchVerschieben(1)} data-testid="verschieben-morgen">
                Morgen
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => batchVerschieben(2)} data-testid="verschieben-uebermorgen">
                Übermorgen
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => batchVerschieben(7)} data-testid="verschieben-woche">
                In einer Woche
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <DropdownMenuItem
                    onSelect={(e) => { e.preventDefault(); setCalendarOpen(true); }}
                    data-testid="verschieben-datum"
                  >
                    <CalendarDays className="w-4 h-4 mr-2" />
                    Datum wählen...
                  </DropdownMenuItem>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="center" side="top">
                  <Calendar
                    mode="single"
                    locale={de}
                    onSelect={batchVerschiebenDatum}
                    disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                  />
                </PopoverContent>
              </Popover>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="w-px h-6 bg-slate-600" />

          {/* Abwählen */}
          <Button
            size="sm"
            variant="ghost"
            className="text-slate-400 hover:text-white hover:bg-slate-800"
            onClick={() => setSelected(new Map())}
            data-testid="batch-deselect-btn"
          >
            <X className="w-4 h-4 mr-1" />
            Auswahl aufheben
          </Button>
        </div>
      )}

      <SammelErledigenDialog open={Boolean(erledigenIds)} ids={erledigenIds || []} onClose={() => setErledigenIds(null)}
        onDone={() => { setErledigenIds(null); setSelected(new Map()); neuLaden(); }} />
      <ZusammenfuehrenDialog open={Boolean(mergeIds)} ids={mergeIds || []} onClose={() => setMergeIds(null)}
        onDone={(r) => { setMergeIds(null); setSelected(new Map()); if (r?.haupt_aufgabe_id) setAufgeklappt(prev => new Set(prev).add(r.haupt_aufgabe_id)); neuLaden(); }} />
    </div>
  );
}
